<template>
  <div class="relative flex gap-3 px-4 py-3.5">
    <UiAvatar
      v-if="notification.kind === 'chat_message'"
      :url="notification.sender_avatar"
      :name="notification.sender_display_name || notification.title"
      size="xs"
      :responsive="false"
      color-coded
    />

    <div
      v-else
      class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
      :class="eventIcon.bg"
    >
      <svg
        class="h-5 w-5"
        :class="eventIcon.fg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        stroke-width="1.5"
      >
        <path stroke-linecap="round" stroke-linejoin="round" :d="eventIcon.path" />
      </svg>
    </div>

    <div
      v-if="notification.kind === 'chat_message'"
      class="min-w-0 flex-1"
      :class="{ 'pr-8': reserveCloseSpace }"
    >
      <div class="flex items-baseline justify-between gap-2">
        <p class="truncate text-h4 font-semibold text-gray-900">
          {{ notification.sender_display_name || notification.title }}
        </p>
        <span
          v-if="!hideTime && !timeAtBottom"
          class="shrink-0 text-h6 text-gray-400"
        >
          {{ formatTimeAgo(notification.created_at, { shortFormat: true }) }}
        </span>
      </div>
      <p class="mt-0.5 truncate text-h6 text-gray-400">
        {{ notification.title }}
      </p>
      <p
        class="mt-1 line-clamp-1 text-h5 text-gray-600"
        :class="{ 'font-semibold': emphasizeUnread && !notification.is_read }"
      >
        {{ displayBody }}
      </p>
      <div
        v-if="!hideTime && timeAtBottom"
        class="mt-1 flex justify-end"
      >
        <span class="shrink-0 text-h6 text-gray-400">
          {{ formatTimeAgo(notification.created_at, { shortFormat: true }) }}
        </span>
      </div>
      <div
        v-if="$slots['trailing-action']"
        class="pointer-events-none absolute right-4 top-8.5"
      >
        <div class="pointer-events-auto">
          <slot name="trailing-action" />
        </div>
      </div>
    </div>

    <div
      v-else
      class="min-w-0 flex-1"
      :class="{ 'pr-8': reserveCloseSpace }"
    >
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0">
          <p class="truncate text-h6 text-gray-400">
            {{ notification.title }}
          </p>
          <p
            class="mt-0.5 text-h4 leading-snug text-gray-900"
            :class="emphasizeUnread && !notification.is_read ? 'font-semibold' : 'font-medium'"
          >
            {{ displayBody }}
          </p>
        </div>
        <span
          v-if="!hideTime && !timeAtBottom"
          class="shrink-0 pt-0.5 text-h6 text-gray-400"
        >
          {{ formatTimeAgo(notification.created_at, { shortFormat: true }) }}
        </span>
      </div>
      <div
        v-if="!hideTime && timeAtBottom"
        class="mt-1 flex justify-end"
      >
        <span class="shrink-0 text-h6 text-gray-400">
          {{ formatTimeAgo(notification.created_at, { shortFormat: true }) }}
        </span>
      </div>
      <div
        v-if="$slots['trailing-action']"
        class="pointer-events-none absolute right-4 top-8.5"
      >
        <div class="pointer-events-auto">
          <slot name="trailing-action" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { UiAvatar } from "@shopexperts/shared";
import { formatTimeAgo } from "@/utils/date";
import {
  getNotificationDisplayBody,
  getNotificationEventIconStyle,
} from "@/v2Projects/utils/notificationPresentation";
import type { DashboardNotification } from "@/v2Projects/types";

const props = withDefaults(defineProps<{
  notification: DashboardNotification;
  emphasizeUnread?: boolean;
  reserveCloseSpace?: boolean;
  timeAtBottom?: boolean;
  hideTime?: boolean;
}>(), {
  emphasizeUnread: true,
  reserveCloseSpace: false,
  timeAtBottom: false,
  hideTime: false,
});

const eventIcon = computed(() => getNotificationEventIconStyle(props.notification.event_type));
const displayBody = computed(() => getNotificationDisplayBody(props.notification));
</script>
